import React, { memo } from 'react';
import styled, { css } from 'styled-components';

// Design tokens for badges
export const badgeVariants = {
  primary: css`
    background: var(--color-blue-100);
    color: var(--color-blue-800);
    border: 1px solid var(--color-blue-200);
  `,
  success: css`
    background: var(--color-green-100);
    color: var(--color-green-800);
    border: 1px solid var(--color-green-200);
  `,
  warning: css`
    background: var(--color-yellow-100);
    color: var(--color-yellow-800);
    border: 1px solid var(--color-yellow-200);
  `,
  danger: css`
    background: var(--color-red-100);
    color: var(--color-red-800);
    border: 1px solid var(--color-red-200);
  `,
  neutral: css`
    background: var(--color-gray-100);
    color: var(--color-gray-800);
    border: 1px solid var(--color-gray-200);
  `
} as const;

export const badgeSizes = {
  sm: css`
    padding: 0.25rem 0.5rem;
    font-size: 0.75rem;
    border-radius: var(--border-radius-sm);
  `,
  md: css`
    padding: 0.375rem 0.75rem;
    font-size: 0.875rem;
    border-radius: var(--border-radius-md);
  `,
  lg: css`
    padding: 0.5rem 1rem;
    font-size: 1rem;
    border-radius: var(--border-radius-md);
  `
} as const;

interface BadgeProps {
  children: React.ReactNode;
  variant?: keyof typeof badgeVariants;
  size?: keyof typeof badgeSizes;
  className?: string;
  'data-testid'?: string;
}

const StyledBadge = styled.span<{
  $variant: keyof typeof badgeVariants;
  $size: keyof typeof badgeSizes;
}>`
  display: inline-flex;
  align-items: center;
  font-family: var(--font-sans);
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.025em;
  white-space: nowrap;
  
  ${({ $variant }) => badgeVariants[$variant]}
  ${({ $size }) => badgeSizes[$size]}
`;

/**
 * Status Badge component for displaying status, priority, or category information
 * 
 * @example
 * <Badge variant="success" size="md">Approved</Badge>
 * <Badge variant="warning" size="sm">Pending</Badge>
 */
export const Badge = memo<BadgeProps>(({
  children,
  variant = 'neutral',
  size = 'md',
  className,
  'data-testid': testId
}) => {
  return (
    <StyledBadge
      className={className}
      $variant={variant}
      $size={size}
      data-testid={testId}
    >
      {children}
    </StyledBadge>
  );
});

Badge.displayName = 'Badge';

// Utility function for mapping status to badge variants
export const getStatusBadgeVariant = (status: string): keyof typeof badgeVariants => {
  const statusMap: Record<string, keyof typeof badgeVariants> = {
    'APPROVED': 'success',
    'PUBLISHED': 'success',
    'ACTIVE': 'success',
    'PENDING_REVIEW': 'warning',
    'DRAFT': 'warning',
    'AI_REVIEW': 'warning',
    'ADMIN_REVIEW': 'warning',
    'REJECTED': 'danger',
    'DELETED': 'danger',
    'BANNED': 'danger',
    'INACTIVE': 'neutral',
    'ARCHIVED': 'neutral'
  };
  
  return statusMap[status.toUpperCase()] || 'neutral';
};

// Utility function for mapping priority to badge variants
export const getPriorityBadgeVariant = (priority: string): keyof typeof badgeVariants => {
  const priorityMap: Record<string, keyof typeof badgeVariants> = {
    'HIGH': 'danger',
    'URGENT': 'danger',
    'MEDIUM': 'warning',
    'LOW': 'neutral',
    'NORMAL': 'neutral'
  };
  
  return priorityMap[priority.toUpperCase()] || 'neutral';
};